import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Building2, GraduationCap, Landmark, Globe, Briefcase, Award, BookOpen, Plane } from 'lucide-react';

const BENEFIT_CATEGORIES = [
  { id: 'all', label: 'All Benefits' },
  { id: 'funding', label: 'Funding & Grants' },
  { id: 'education', label: 'Education' },
  { id: 'coworking', label: 'Co-Working' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'accelerator', label: 'Accelerators' },
];

const BENEFITS = [
  // FUNDING & GRANTS
  {
    id: 1, category: 'funding',
    title: 'Standard Chartered Women in Tech UAE Accelerator',
    provider: 'Standard Chartered + C3 + Village Capital',
    region: 'UAE & Middle East',
    value: 'Up to AED 550,000 (~$150K) Equity-Free',
    description: 'Equity-free funding, mentorship, training, and investor networks for women-led tech startups. Seventh cohort running. 400+ women entrepreneurs supported regionally with AED 2.2M annually.',
    eligibility: 'Women-led tech startups in the UAE',
    link: 'https://fintechnews.ae/26023/fintech/standard-chartered-c3-women-in-tech-uae-accelerator-2025/',
    icon: Landmark, color: '#2E8B57', tag: 'GRANTS',
  },
  {
    id: 2, category: 'funding',
    title: 'She WINS Arabia — IFC Women Startup Programme',
    provider: 'IFC + Abu Dhabi Global Market + We-Fi',
    region: 'MENA (15+ Countries)',
    value: 'Training + Investor Access',
    description: 'Business training, investor pitches, and fund manager connections for women-led startups across MENA. Expanded to 100+ startups in 15+ countries including UAE, Saudi Arabia, Qatar, and Oman.',
    eligibility: 'Women-led startups across MENA region',
    link: 'https://www.ifc.org/en/where-we-work/middle-east/promoting-inclusion/she-wins-arabia',
    icon: Globe, color: '#4A6FA5', tag: 'PROGRAMME',
  },
  {
    id: 3, category: 'funding',
    title: 'Visa She\'s Next — Women Entrepreneur Grants',
    provider: 'Visa + SAB + Monsha\'at',
    region: 'Saudi Arabia & Middle East',
    value: 'Grants + Recognition',
    description: 'Supporting women-led businesses via grants and recognition across fintech, e-commerce, and ed-tech sectors. Applications open annually.',
    eligibility: 'Women-led businesses in Saudi Arabia / Middle East',
    link: 'https://sa.visamiddleeast.com/en_SA/run-your-business/she-is-next-entrepreneurship.html',
    icon: Award, color: '#C25E3E', tag: 'GRANTS',
  },
  {
    id: 4, category: 'funding',
    title: 'We-Fi — Women Entrepreneurs Finance Initiative',
    provider: 'World Bank Group',
    value: '$1B+ Mobilised Globally',
    region: 'Global (Focus: MENA & APAC)',
    description: 'The largest global fund dedicated to women entrepreneurs. Mobilised over $1 billion in finance for women-led SMEs through partners including Islamic Development Bank.',
    eligibility: 'Women entrepreneurs globally, priority: developing markets',
    link: 'https://we-fi.org/',
    icon: Landmark, color: '#2E8B57', tag: 'GLOBAL FUND',
  },

  // EDUCATION & SCHOLARSHIPS
  {
    id: 5, category: 'education',
    title: 'Daughters for Life Scholarship',
    provider: 'Daughters for Life Foundation',
    region: 'MENA Women',
    value: 'Fully Funded (Tuition + Housing + Travel)',
    description: 'Full scholarships covering tuition, housing, meals, and travel for female students from the Middle East and North Africa at partner universities worldwide.',
    eligibility: 'Female MENA students with academic excellence and financial need',
    link: 'https://daughtersforlife.com/programs/scholarship/',
    icon: GraduationCap, color: '#7A5BA5', tag: 'SCHOLARSHIP',
  },
  {
    id: 6, category: 'education',
    title: 'ASEAN-UK SAGE Women in STEM Scholarships',
    provider: 'UK Government + ASEAN',
    region: 'ASEAN Countries',
    value: 'Fully Funded Master\'s Degree',
    description: '11 fully funded Master\'s scholarships for women from ASEAN states and Timor-Leste to study STEM subjects at UK universities.',
    eligibility: 'Women from ASEAN member states',
    link: 'https://asean.org/asean-uk-sage-women-in-stem-scholarships-2025-open-to-empower-women-to-lead-in-stem/',
    icon: GraduationCap, color: '#7A5BA5', tag: 'SCHOLARSHIP',
  },
  {
    id: 7, category: 'education',
    title: 'MEPI Tomorrow\'s Leaders Graduate Programme',
    provider: 'U.S. Department of State',
    region: 'Middle East & North Africa',
    value: 'Fully Funded Graduate Studies',
    description: 'Supports women from MENA for graduate studies at partner universities in the United States. Focus on underrepresented communities and emerging leaders.',
    eligibility: 'Women from MENA countries, demonstrated leadership',
    link: 'https://mepi.state.gov/tomorrows-leaders-program/',
    icon: GraduationCap, color: '#7A5BA5', tag: 'SCHOLARSHIP',
  },

  // LEADERSHIP PROGRAMMES
  {
    id: 8, category: 'leadership',
    title: 'Obama Foundation Leaders — Asia-Pacific 2026-2027',
    provider: 'Obama Foundation',
    region: 'Asia-Pacific',
    value: 'Fully Funded Virtual Leadership Programme',
    description: 'Fully funded virtual leadership programme (September 2026 — May 2027) for Asia-Pacific changemakers aged 24-45. Includes coaching, networking, and community building.',
    eligibility: 'Changemakers aged 24-45 in Asia-Pacific',
    link: 'https://www.obama.org/leaders/',
    icon: Award, color: '#C25E3E', tag: 'LEADERSHIP',
  },
  {
    id: 9, category: 'leadership',
    title: 'Harpswell ASEAN Women\'s Leadership Summit 2026',
    provider: 'Harpswell Foundation',
    region: 'ASEAN + Nepal + Papua New Guinea',
    value: 'Fully Funded (Travel + Accommodation)',
    description: 'Fully funded hybrid programme with virtual sessions (July-October 2026) and in-person summit in Phnom Penh, Cambodia (October 30 — November 8). Travel, accommodation, and meals covered.',
    eligibility: 'Women leaders from ASEAN countries, Nepal, Papua New Guinea',
    link: 'https://opportunitiesforyouth.org/2026/03/03/harpswell-asean-womens-leadership-summit-2026-fully-funded-program-in-cambodia-apply-by-april-19-2026/',
    icon: Globe, color: '#4A6FA5', tag: 'SUMMIT',
  },
  {
    id: 10, category: 'leadership',
    title: 'EY Entrepreneurial Winning Women — Asia-Pacific',
    provider: 'EY (Ernst & Young)',
    region: 'Asia-Pacific',
    value: 'Mentorship + EY Network Access',
    description: 'Identifies and supports high-potential women entrepreneurs across Asia-Pacific with mentorship, media exposure, and access to EY\'s global network of investors and advisors.',
    eligibility: 'Women entrepreneurs with established businesses in APAC',
    link: 'https://www.ey.com/en_us/entrepreneurial-winning-women-asia-pacific/class-of-2026/',
    icon: Briefcase, color: '#1A8A7D', tag: 'MENTORSHIP',
  },
  {
    id: 11, category: 'leadership',
    title: 'TiE Women MENA Programme 2025',
    provider: 'TiE Global',
    region: 'Middle East & North Africa',
    value: 'Mentorship + Network + Pitch',
    description: 'Empowers women innovators across the Middle East and North Africa with mentorship from seasoned entrepreneurs, pitch opportunities, and access to TiE\'s global chapter network.',
    eligibility: 'Women entrepreneurs and innovators in MENA',
    link: 'https://events.tie.org/twe25',
    icon: Award, color: '#C25E3E', tag: 'PROGRAMME',
  },

  // CO-WORKING
  {
    id: 12, category: 'coworking',
    title: 'The Bureau — Women-Focused Co-Working, Dubai',
    provider: 'The Bureau (Nikita & Rhea Patel)',
    region: 'Dubai, UAE',
    value: 'Women-Only Workspace',
    description: 'Dubai\'s first female-focused co-working space, founded in 2022. Designed for women entrepreneurs, freelancers, and professionals with networking events, workshops, and a supportive community.',
    eligibility: 'All women professionals and entrepreneurs',
    link: 'https://thebureau.ae/',
    icon: Building2, color: '#C4792E', tag: 'CO-WORKING',
  },
  {
    id: 13, category: 'coworking',
    title: 'DIFC Innovation Hub — Fintech Co-Working',
    provider: 'Dubai International Financial Centre',
    region: 'Dubai, UAE',
    value: 'Fintech Workspace + Licensing',
    description: 'Co-working and innovation space within DIFC offering affordable licensing, mentorship, and access to the MENA financial ecosystem. Home to 130+ fintech firms.',
    eligibility: 'Fintech startups and entrepreneurs',
    link: 'https://www.difc.ae/business/innovation-hub/',
    icon: Building2, color: '#C4792E', tag: 'CO-WORKING',
  },

  // ACCELERATORS
  {
    id: 14, category: 'accelerator',
    title: 'UNIDO Women Entrepreneurial Challenge',
    provider: 'UNIDO + WeConnect International',
    region: 'Global (2026 in Bahrain)',
    value: 'B2B Partnerships + Mentorship',
    description: 'International forum for women entrepreneurs to form B2B partnerships. 2026 edition in Bahrain brought together women from UAE, Morocco, and across MENA for networking, finance access, and regional expansion.',
    eligibility: 'Women-owned businesses globally',
    link: 'https://news.un.org/en/story/2026/02/1166964',
    icon: Globe, color: '#4A6FA5', tag: 'B2B',
  },
  {
    id: 15, category: 'accelerator',
    title: 'Vital Voices Global Fellowship',
    provider: 'Vital Voices',
    region: 'Global (Strong APAC & MENA presence)',
    value: 'Yearlong Fellowship + Global Network',
    description: 'Yearlong fellowship connecting emerging women leaders with mentors from Fortune 500 companies, governments, and NGOs. Strong alumni network across Asia-Pacific and Middle East.',
    eligibility: 'Women leaders with demonstrated social impact',
    link: 'https://www.vitalvoices.org/program/vital-voices-global-fellowship/',
    icon: Award, color: '#C25E3E', tag: 'FELLOWSHIP',
  },
];

const BenefitsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const filtered = activeCategory === 'all' ? BENEFITS : BENEFITS.filter(b => b.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20" data-testid="benefits-page">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-16">
        <div className="max-w-3xl">
          <p className="text-[12px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>
            MEMBER BENEFITS
          </p>
          <h1 className="text-4xl lg:text-[64px] leading-[0.95] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
            Real opportunities.<br /><span className="text-[#C25E3E]">Real partners.</span>
          </h1>
          <p className="text-lg leading-relaxed max-w-xl" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
            Curated grants, scholarships, accelerators, and workspaces across Asia-Pacific and the Middle East — exclusively for the She Built This community.
          </p>
        </div>
      </section>

      {/* Stats ribbon */}
      <section className="bg-[#1A1A1A] py-6 mb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap justify-center gap-12 text-center">
          {[
            { num: '15', label: 'PROGRAMMES' },
            { num: '$1B+', label: 'FUNDING AVAILABLE' },
            { num: '25+', label: 'COUNTRIES COVERED' },
            { num: '100%', label: 'FREE TO ACCESS' },
          ].map(s => (
            <div key={s.label}>
              <p className="text-3xl text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.num}</p>
              <p className="text-[9px] tracking-[0.25em] uppercase text-white/50" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {BENEFIT_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`h-10 px-5 text-[11px] tracking-[0.12em] uppercase border transition-all ${activeCategory === cat.id ? 'bg-[#C25E3E] border-[#C25E3E] text-white' : 'border-[#1A1A1A]/15 text-[#4A4A4A] hover:border-[#C25E3E] hover:text-[#C25E3E]'}`}
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(benefit => {
            const Icon = benefit.icon;
            return (
              <div key={benefit.id} className="group bg-white border border-[#1A1A1A]/8 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col" data-testid={`benefit-${benefit.id}`}>
                {/* Header */}
                <div className="p-6 pb-0">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FAF9F6]" style={{ color: benefit.color }}>
                      <Icon size={24} />
                    </div>
                    <span className="text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 border font-semibold" style={{ fontFamily: "'Outfit', sans-serif", color: benefit.color, borderColor: benefit.color }}>
                      {benefit.tag}
                    </span>
                  </div>
                  <h3 className="text-[17px] leading-tight mb-2 group-hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-[12px] mb-1" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: '#C25E3E' }}>
                    {benefit.provider}
                  </p>
                  <p className="text-[11px] text-[#888] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {benefit.region}
                  </p>
                </div>

                {/* Value badge */}
                <div className="mx-6 mb-4 px-4 py-2.5 bg-[#F3EFE9]">
                  <p className="text-[13px] font-semibold" style={{ fontFamily: "'Outfit', sans-serif", color: '#1A1A1A' }}>
                    {benefit.value}
                  </p>
                </div>

                {/* Description */}
                <div className="px-6 pb-4 flex-1">
                  <p className="text-[13px] leading-relaxed" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
                    {benefit.description}
                  </p>
                </div>

                {/* Eligibility */}
                <div className="px-6 pb-4">
                  <p className="text-[10px] tracking-wide uppercase text-[#888] mb-1" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Eligibility</p>
                  <p className="text-[12px]" style={{ fontFamily: "'Outfit', sans-serif", color: '#4A4A4A' }}>{benefit.eligibility}</p>
                </div>

                {/* CTA */}
                <div className="px-6 pb-6 mt-auto">
                  <a href={benefit.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-10 px-6 bg-[#1A1A1A] text-white text-[11px] tracking-[0.12em] uppercase hover:bg-[#C25E3E] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }} data-testid={`apply-${benefit.id}`}>
                    Learn More & Apply <ExternalLink size={12} className="ml-2" />
                  </a>
                </div>

                {/* Bottom accent */}
                <div className="h-[3px] bg-[#C25E3E] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            );
          })}
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-20">
        <div className="bg-[#1A1A1A] p-12 lg:p-16 text-center">
          <h2 className="text-3xl lg:text-4xl text-white mb-4 tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Want to partner with She Built This?
          </h2>
          <p className="text-base text-white/60 max-w-lg mx-auto mb-8" style={{ fontFamily: "'Lora', serif" }}>
            We partner with banks, universities, co-working spaces, and accelerators across Asia-Pacific and the Middle East.
          </p>
          <Link to="/participate" className="inline-flex items-center h-14 px-10 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.18em] hover:bg-white hover:text-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
            Become a Partner <ArrowRight size={15} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BenefitsPage;
